import { useLiveQuery } from "dexie-react-hooks";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { computeAccountingMonth, shiftMonth } from "@/domain/accounting-period";
import { INCOME_TYPES, type IncomeType } from "@/domain/income";
import type { Income } from "@/domain/model";
import { sum } from "@/domain/money";
import { saveIncome, voidIncome } from "@/data/ledger";
import { formatDate, formatEurPlain, formatMonth, formatMonthTitle } from "@/lib/format";
import { INCOME_TYPE_LABELS, SYNC_STATUS_LABELS } from "@/lib/labels";
import { Badge, Button, ConfirmButton, EmptyState, Field, Notice, PageHeader, Panel, PeriodSwitcher, SelectInput, TextArea, TextInput } from "@/ui/primitives";
import { db, monthParam, useIncomes, useToday } from "../hooks";

export function IncomeListPage() {
  const { month: current } = useToday();
  const [params] = useSearchParams();
  const month = monthParam(params.get("mese") ?? undefined, current);
  const incomes = useIncomes(month);
  if (!incomes) return <p className="text-muted">Caricamento…</p>;
  const total = sum(incomes.map((i) => i.amount));

  return (
    <div>
      <PageHeader
        title="Entrate"
        description="Il registro delle entrate è la fonte dei conti: stipendio, rimborsi, regali e altro, ciascuno con la sua data."
        actions={
          <Link to="/entrate/nuova" className="inline-flex min-h-11 items-center rounded-lg bg-petrol px-4 font-semibold text-white">
            Nuova entrata
          </Link>
        }
      />
      <PeriodSwitcher
        label={formatMonthTitle(month)}
        previousHref={`/entrate?mese=${shiftMonth(month, -1)}`}
        nextHref={`/entrate?mese=${shiftMonth(month, 1)}`}
        previousLabel={`Entrate di ${formatMonth(shiftMonth(month, -1))}`}
        nextLabel={`Entrate di ${formatMonth(shiftMonth(month, 1))}`}
      >
        <p className="amount text-sm text-muted">
          {incomes.length} entrate, {formatEurPlain(total)}
        </p>
      </PeriodSwitcher>
      {incomes.length === 0 ? (
        <EmptyState title={`Nessuna entrata in ${formatMonth(month)}`}>Usa il pulsante + per registrarne una.</EmptyState>
      ) : (
        <ul className="divide-y divide-line rounded-xl border border-line bg-surface">
          {incomes.map((i) => (
            <li key={i.id}>
              <Link to={`/entrate/${i.id}`} className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-ground">
                <div className="min-w-0">
                  <p className="truncate font-medium">{i.description}</p>
                  <p className="text-sm text-muted">{INCOME_TYPE_LABELS[i.type]}</p>
                  <p className="mt-1 flex flex-wrap gap-1">
                    {i.source === "excel_import" ? <Badge>storico Excel</Badge> : <Badge>{SYNC_STATUS_LABELS[i.sync.status]}</Badge>}
                    {i.accountingRule === "manual_override" ? <Badge tone="petrol">ricevuta il {formatDate(i.date)}</Badge> : null}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="amount font-semibold">{formatEurPlain(i.amount)}</p>
                  <p className="amount text-sm text-muted">{i.dateCertain ? formatDate(i.date) : formatMonthTitle(i.accountingMonth)}</p>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface IncomeForm {
  amount: string;
  date: string;
  type: IncomeType;
  description: string;
  notes: string;
  overrideEnabled: boolean;
  override: string;
}

function fromIncome(i: Income): IncomeForm {
  return {
    amount: i.amount.replace(".", ","),
    date: i.date,
    type: i.type,
    description: i.description,
    notes: i.notes ?? "",
    overrideEnabled: i.accountingRule === "manual_override",
    override: i.accountingRule === "manual_override" ? i.accountingMonth : "",
  };
}

export function IncomeFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { today } = useToday();
  const existing = useLiveQuery(async () => (id ? ((await db.incomes.get(id)) ?? null) : null), [id]);
  const [edited, setEdited] = useState<IncomeForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const initial: IncomeForm | null = existing
    ? fromIncome(existing)
    : id
      ? null
      : { amount: "", date: today, type: "salary", description: "Stipendio", notes: "", overrideEnabled: false, override: "" };
  const form = edited ?? initial;

  if (existing === undefined) return <p className="text-muted">Caricamento…</p>;
  if (id && existing === null) return <Notice tone="error">Entrata non trovata.</Notice>;
  if (!form) return <p className="text-muted">Caricamento…</p>;
  const readOnly = existing?.source === "excel_import" || existing?.status === "void";
  let previewMonth: string | null = null;
  try {
    previewMonth = computeAccountingMonth({ purchaseDate: form.date, rule: "same_month", override: form.overrideEnabled ? form.override : null }).accountingMonth;
  } catch {
    previewMonth = null;
  }
  const set = (patch: Partial<IncomeForm>) => setEdited({ ...form, ...patch });

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!form) return;
    setError(null);
    setSaving(true);
    try {
      const saved = await saveIncome(db, {
        id: existing?.id,
        date: form.date,
        amount: form.amount,
        type: form.type,
        description: form.description,
        notes: form.notes,
        accountingMonthOverride: form.overrideEnabled ? form.override : null,
      });
      navigate(`/entrate?mese=${saved.accountingMonth}`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <PageHeader title={existing ? "Entrata" : "Nuova entrata"} />
      {existing?.source === "excel_import" ? (
        <Notice>
          Importata dal file Excel ({existing.legacy?.sheet}!{existing.legacy?.cell}). Il file non registra la data esatta: è indicato il mese.
        </Notice>
      ) : null}
      {existing?.status === "void" ? <Notice>Entrata annullata.</Notice> : null}
      <Panel className="mt-4">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <Field label="Importo (€)">
            <TextInput
              inputMode="decimal"
              required
              autoFocus={!existing}
              placeholder="0,00"
              value={form.amount}
              disabled={readOnly}
              onChange={(e) => set({ amount: e.target.value })}
              className="amount text-2xl font-semibold"
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Data">
              <TextInput type="date" required value={form.date} disabled={readOnly} onChange={(e) => set({ date: e.target.value })} />
            </Field>
            <Field label="Tipo">
              <SelectInput
                value={form.type}
                disabled={readOnly}
                onChange={(e) => {
                  const type = e.target.value as IncomeType;
                  set({ type, description: form.description === INCOME_TYPE_LABELS[form.type] || !form.description ? INCOME_TYPE_LABELS[type] : form.description });
                }}
              >
                {INCOME_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {INCOME_TYPE_LABELS[t]}
                  </option>
                ))}
              </SelectInput>
            </Field>
          </div>
          <Field label="Descrizione">
            <TextInput required value={form.description} disabled={readOnly} onChange={(e) => set({ description: e.target.value })} />
          </Field>
          <Field label="Note" hint="Facoltative">
            <TextArea value={form.notes} disabled={readOnly} onChange={(e) => set({ notes: e.target.value })} />
          </Field>
          <div className="rounded-lg bg-petrol-soft px-4 py-3" aria-live="polite">
            {previewMonth ? (
              <p>
                Conta in <strong className="font-ledger text-lg">{formatMonth(previewMonth)}</strong>
              </p>
            ) : null}
            {!readOnly ? (
              <label className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                <input type="checkbox" checked={form.overrideEnabled} onChange={(e) => set({ overrideEnabled: e.target.checked, override: e.target.checked ? (previewMonth ?? "") : "" })} />
                Assegna a un altro mese (es. stipendio pagato a fine mese per il mese dopo)
                {form.overrideEnabled ? (
                  <input type="month" required value={form.override} onChange={(e) => set({ override: e.target.value })} className="rounded-md border border-line bg-surface px-2 py-1" />
                ) : null}
              </label>
            ) : null}
          </div>
          {error ? <Notice tone="error">{error}</Notice> : null}
          {!readOnly ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Button type="submit" disabled={saving} className="flex-1 sm:flex-none">
                {saving ? "Salvataggio…" : existing ? "Salva modifiche" : "Salva entrata"}
              </Button>
              {existing ? (
                <ConfirmButton
                  label="Annulla entrata"
                  confirmLabel="Conferma annullamento"
                  onConfirm={async () => {
                    await voidIncome(db, existing.id);
                    navigate(`/entrate?mese=${existing.accountingMonth}`);
                  }}
                />
              ) : null}
            </div>
          ) : null}
        </form>
      </Panel>
    </div>
  );
}
